import { KeyboardData, useKeyboardStore } from '../../../hooks/stores/keyboardStore';
import { LauncherElementData, useLauncherElementsStore } from '../../../hooks/stores/launcherElementsStore';
import { useEffect, useMemo } from 'react';

import { findNodeLevel } from '../../../functions/launcherElement/findNodeLevel';
import { launcherElementInfo } from '../../../info/launcherElementInfo';
import styled from 'styled-components';
import { useLauncherStatesStore } from '../../../hooks/stores/launcherStatesStore';
import { useLauncherWindowStore } from '../../../hooks/stores/launcherWindowStore';
import { useScene } from '../../../context/sceneContext';
import { useUIStore } from '../../../hooks/stores/uiStore';

export const ElementTreeNode = ({ node, dragHandle }: any) => {
  const scene = useScene();
  const [keyboardData] = useKeyboardStore<KeyboardData>();
  const [UIData] = useUIStore();
  const [launcherWindowData] = useLauncherWindowStore();
  const [launcherElementsData] = useLauncherElementsStore<Record<string, LauncherElementData>>();
  const [launcherStatesData] = useLauncherStatesStore();
  const element = launcherElementsData[node.data.id];
  const { id, type, name, props: { variable, commands, text } = {} } = element || ({ props: {} } as LauncherElementData);

  const commandText =
    commands && commands.length > 0
      ? '( ' +
      launcherElementInfo.button.commands?.[commands?.[0].type] +
      ' )' +
      (commands.length > 1 ? ' +' + (commands.length - 1) : '')
      : '';
  const variableText = variable ? '[ ' + launcherElementInfo.variableText.variables?.[variable] + ' ]' || variable : '';
  const hrefText = element?.props?.href || '';

  const isLicensePopup = id === 'appLicenseKeyPopup';
  // const iconClassName = isLicensePopup ? launcherElementInfo.licensePopup.iconClassName : launcherElementInfo[type]?.iconClassName; // TODO: add back
  const iconClassName = launcherElementInfo[type]?.iconClassName;
  const nodeLevel = findNodeLevel(launcherWindowData.children, node.data.id);
  const isElementVisible = useMemo(
    () => scene.getElementComputedVisibility(node.data.id) !== false,
    [launcherStatesData, launcherElementsData]
  );

  const drawGroupLines = () => {
    if (!nodeLevel) return null;

    const lines = [];
    for (let i = 0; i < nodeLevel; i++) {
      lines.push(
        <GroupLine
          key={i}
          $isLastLine={i === nodeLevel - 1}
          $isLast={node.nextSibling === null && !node.isOpen}
          style={{ opacity: 1 - 1 / (i + 1.5) }}
        />
      );
    }

    return lines;
  };

  const handleClick = () => {
    const id = node.data.id;

    if (keyboardData.keysDown?.includes('ControlLeft')) {
      if (UIData.selectedElementIDs.includes(id)) {
        scene.deselectElement(id);
      } else {
        scene.selectElement(id);
      }
      return;
    }
    if (keyboardData.keysDown?.includes('ShiftLeft')) {
      // TODO: Select between IDs
      return;
    }

    scene.selectOneElement(id);
  };

  // Sync with selecting on the scene
  useEffect(() => {
    const id = node.data.id;
    if (UIData.selectedElementIDs.includes(id)) {
      node.selectMulti();
    } else {
      node.deselect();
    }
  }, [UIData.selectedElementIDs]);

  return (
    <Container
      ref={node.isEditing ? null : dragHandle}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleClick();
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        handleClick();
      }}
      ui-element='elementTreeNode'
      launcher-element-id={node.data.id}
      launcher-element-type={type}
      $isFocused={node.isFocused}
      $isSelected={node.isSelected}
      $isHovered={UIData.hoveredLauncherElementIDs.includes(node.data.id)}
      $willReceiveDrop={node.willReceiveDrop}
      $nodeLevel={nodeLevel || 0}
      $isElementVisible={isElementVisible}
    >
      {drawGroupLines()}
      <ArrowContainer
        onClick={(e) => {
          if (!node.isLeaf) {
            e.stopPropagation();
            node.toggle();
          }
        }}
      >
        {!node.isLeaf ? (
          <GroupArrow $isOpen={node.isOpen}>
            <i className="icon-arrow-right" />
          </GroupArrow>
        ) : (
          <div style={{ width: '14px', minWidth: '14px' }} />
        )}
        <ChildCount>{!node.isLeaf ? node.children?.length || '' : ''}</ChildCount>
      </ArrowContainer>
      <Icon>{iconClassName && <i className={iconClassName} />}</Icon>
      {node.isEditing ? (
        <Input
          defaultValue={name}
          onBlur={(e) => node.submit(e.target.value)}
          onKeyDown={(e) => {
            e.key === 'Enter' && e.currentTarget.blur();
          }}
          autoFocus
          spellCheck={false}
        />
      ) : (
        <Text
          style={{
            fontStyle: !name && text ? 'italic' : undefined,
            minWidth: '100px',
            minHeight: '15px',
          }}
          onDoubleClick={() => node.edit()}
        >
          {name || variableText || commandText || hrefText || (text && `"${text}"`)}
        </Text>
      )}
    </Container>
  );
};

const Container = styled.div<{
  $isFocused?: boolean;
  $isSelected?: boolean;
  $isHovered?: boolean;
  $willReceiveDrop?: boolean;
  $nodeLevel: number;
  $isElementVisible: boolean;
}>`
  position: relative;
  box-sizing: border-box;
  height: 100%;
  margin-left: var(--padding-1);
  margin-right: calc(var(--padding-1) / 2);
  padding-right: calc(var(--padding-1) / 2);

  display: flex;
  align-items: center;
  border-radius: var(--radius-2);

  color: var(--main-2);

  &:hover:not($isSelected) {
    background-color: var(--neutral-3);
    color: var(--main-1);
  }
  ${({ $isHovered }) =>
    $isHovered &&
    `
    &:after {
      content: '';
      pointer-events: none;
      box-sizing: border-box;
      z-index: 1;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border: 1px solid var(--neutral-3);
      border-radius: var(--radius-2);
    }
    color: var(--main-1);
  `}

  ${({ $isFocused }) =>
    $isFocused &&
    `
    color: var(--main-1);
    &:after {
      content: '';
      pointer-events: none;
      box-sizing: border-box;
      z-index: 1;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border: 1px solid var(--neutral-3);
      border-radius: var(--radius-2);
    }
  `}

  ${({ $isSelected }) =>
    $isSelected &&
    `
    background-color: var(--neutral-1);
    color: var(--primary-1);
  `}

  ${({ $willReceiveDrop, $nodeLevel }) =>
    $willReceiveDrop &&
    `
    &:after {
      content: '';
      pointer-events: none;
      box-sizing: border-box;
      z-index: 1;
      position: absolute;
      top: 0;
      left: ${$nodeLevel * 14}px;
      width: calc(100% - ${$nodeLevel * 14}px);
      height: 100%;
      border: 1px dashed var(--main-1);
      border-radius: var(--radius-2);
    }
  `}

  ${({ $isElementVisible }) =>
    !$isElementVisible &&
    `
    opacity: 0.3;
  `}
`;

const Icon = styled.div`
  font-size: 18px;
  opacity: 0.5;
`;

const Text = styled.span`
  max-width: 400px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  padding-right: 2px;

  font-size: 12px;
  font-weight: 400;
  transform: translate3d(0, 0, 0);
`;

const Input = styled.input`
  box-sizing: border-box;
  width: 100%;
  height: calc(100% - 2px);
  padding: 0 5px;
  border: 1px solid var(--primary-1);
  outline: none;
  background-color: var(--neutral-1);
  border-radius: var(--radius-2);
  color: var(--main-1);
`;

const GroupLine = styled.div<{ $isLastLine?: boolean; $isLast?: boolean }>`
  position: relative;
  width: 14px;
  min-width: 14px;
  height: 100%;

  display: flex;
  justify-content: center;
  align-items: center;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    width: 1px;
    height: 100%;
    background-color: var(--neutral-4);

    ${({ $isLast, $isLastLine }) => $isLast && $isLastLine && `height: 50%;`}
  }

  ${({ $isLastLine }) =>
    $isLastLine &&
    `
      &:after {
        content: '';
        position: absolute;
        width: 5px;
        height: 1px;
        background-color: var(--neutral-4);
        translate: 50% 0;
      }
    `}
`;

const ArrowContainer = styled.div`
  display: flex;
  align-items: center;
  color: var(--main-2);
  cursor: pointer;

  &:hover {
    color: var(--main-1);
  }
`;

const GroupArrow = styled.div<{ $isOpen?: boolean }>`
  box-sizing: border-box;
  width: 14px;
  min-width: 14px;
  height: 100%;
  padding-left: 1px;
  border-radius: 3px;

  display: flex;
  justify-content: center;
  align-items: center;

  i {
    display: block;
    padding: 3px;
    transform: rotate(0deg);
    font-size: 9px;
    transition: transform 0.3s ease;
  }

  ${({ $isOpen }) =>
    $isOpen &&
    `
    i {
      transform: rotate(90deg);
    }
  `}
`;

const ChildCount = styled.span`
  font-size: 10px;
  opacity: 0.6;
  width: 5px;
  padding-left: 2px;
  margin-right: 4px;
`;
